// Split requests between partners: the share maths and every line of split copy. Shared by the
// Worker (routes/splits.ts) and the PWA, so both always agree on the numbers.
import type { TxSplitDto } from './api';

/**
 * Can a purchase like this be split at all? Spending, yes. Money set aside (a set-aside category, or
 * an entry logged by Done) only as an entry of a transfer item, whose percent it remembers
 * (`items.split_percent`); a plain set-aside category or a loose transfer, no.
 */
export const splitAllowed = (t: { source: string; setAside: boolean; itemKind: 'expense' | 'transfer' | null }) =>
  t.source !== 'transfer' && !t.setAside ? true : t.itemKind === 'transfer';

/** Accepting into money set aside: what it does to my budget. */
export const ACCEPT_SET_ASIDE = 'It counts as money set aside, never as spending.';

/**
 * The requester's share is picked in whole percent, 0 to 99. 50 is the default. 0 is "All
 * Tiffany's": the whole charge is my partner's, nothing counts in my budget (migration 0046). Not on
 * the Done sheet: a transfer item's entry is my own plan, so it takes 1 to 99 there.
 */
export const SPLIT_DEFAULT_PERCENT = 50;
export const ALL_THEIRS_PERCENT = 0;
export const validSplitPercent = (pct: number, allowAllTheirs = true) => Number.isInteger(pct) && pct >= (allowAllTheirs ? 0 : 1) && pct <= 99;
/** "All Tiffany's": the chip, and the small print while waiting or accepted. */
export const ALL_THEIRS = (partner: string) => `All ${partner}'s`;
export const ALL_THEIRS_LINE = (partner: string) => `${partner} pays all of it; nothing counts in your budget.`;
export const SPLIT_PERCENT_HINT = 'Pick your share: 0 to 99%.';
export const DONE_PERCENT_HINT = 'Pick your share: 1 to 99%.';
export const ALL_THEIRS_NOT_SET_ASIDE = (partner: string) => `${ALL_THEIRS(partner)} is not for money you set aside: pick your share, 1 to 99%.`;
export const SPLIT_NOTE_MAX = 200;

/**
 * Shares of `fullCents` when the requester keeps `myPercent`. The recipient's share is rounded
 * down, so the odd cent goes to the requester, and the two always add up to the full amount.
 * The recipient's share is never 0; mine is 0 only at 0% ("All Tiffany's"). null when the amount
 * is too small to split that way.
 */
export function splitShares(fullCents: number, myPercent: number): { mineCents: number; theirsCents: number } | null {
  if (!Number.isInteger(fullCents) || !validSplitPercent(myPercent) || fullCents < 2) return null;
  const theirsCents = Math.floor((fullCents * (100 - myPercent)) / 100);
  const mineCents = fullCents - theirsCents;
  if (theirsCents < 1 || (myPercent > 0 && mineCents < 1)) return null;
  return { mineCents, theirsCents };
}

/**
 * The percent a request was sent with, from its stored shares: the whole percent whose shares give
 * the same requester share (the closest one when several do), so a re-sent request keeps it.
 */
export function percentOf(fullCents: number, mineCents: number): number {
  // "All Tiffany's" stays 0% (never the nearest 1%) when it is sent again or moved by the cron.
  if (mineCents === 0) return ALL_THEIRS_PERCENT;
  const exact = mineCents * 100 / fullCents;
  let best = Math.min(99, Math.max(1, Math.round(exact)));
  for (let p = 1; p <= 99; p++) {
    if (splitShares(fullCents, p)?.mineCents !== mineCents) continue;
    if (splitShares(fullCents, best)?.mineCents !== mineCents || Math.abs(p - exact) < Math.abs(best - exact)) best = p;
  }
  return best;
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * The small print under a split purchase whose main number is my share (or, once declined or
 * cancelled, the full amount again): "of $20.00, split with Tiffany, waiting".
 */
export function splitSmallPrint(s: TxSplitDto): string {
  if (s.direction === 'received') return `of ${dollars(s.fullCents)}, split with ${s.partnerName}`;
  if (s.endedReason === 'removed') return `Split with ${s.partnerName} removed, full amount counts`;
  if (s.myShareCents === 0 && (s.status === 'pending' || s.status === 'accepted'))
    return `${ALL_THEIRS(s.partnerName)}, ${s.status === 'pending' ? 'waiting' : 'accepted'}`;
  switch (s.status) {
    case 'pending':
      return `of ${dollars(s.fullCents)}, split with ${s.partnerName}, waiting`;
    case 'accepted':
      return `of ${dollars(s.fullCents)}, split with ${s.partnerName}, accepted`;
    case 'declined':
      return `Split with ${s.partnerName} declined, full amount counts`;
    case 'cancelled':
      return `Split with ${s.partnerName} cancelled, full amount counts`;
  }
}

/** The line under a split purchase, on both sides. */
export function splitLine(s: TxSplitDto): string {
  if (s.direction === 'received') return `Your share of a ${dollars(s.fullCents)} purchase ${s.partnerName} split with you.`;
  if (s.endedReason === 'removed') return `Split with ${s.partnerName} removed from this purchase. The full amount counts in your budget.`;
  if (s.myShareCents === 0 && (s.status === 'pending' || s.status === 'accepted'))
    return `${ALL_THEIRS(s.partnerName)}, ${s.status === 'pending' ? 'waiting' : 'accepted'}. ${ALL_THEIRS_LINE(s.partnerName)}`;
  switch (s.status) {
    case 'pending':
      return `Split with ${s.partnerName}, waiting. Your share ${dollars(s.myShareCents)} counts in your budget.`;
    case 'accepted':
      return `Split with ${s.partnerName}, accepted. Your share ${dollars(s.myShareCents)} counts in your budget.`;
    case 'declined':
      return `${s.partnerName} declined the split. The full amount is back in your budget.`;
    case 'cancelled':
      return `${cancelReason(s.endedReason, s.partnerName)} The full amount is back in your budget.`;
  }
}

function cancelReason(reason: TxSplitDto['endedReason'], partner: string): string {
  switch (reason) {
    case 'edited':
      return `Split with ${partner} cancelled because the purchase was edited.`;
    case 'deleted':
      return `Split with ${partner} cancelled because the purchase was deleted.`;
    case 'unlinked':
      return `Split with ${partner} cancelled because you are no longer linked.`;
    case 'account':
      return `Split with ${partner} cancelled because their account was deleted.`;
    case 'removed':
      return `Split with ${partner} removed from this purchase.`;
    default:
      return `Split with ${partner} cancelled.`;
  }
}

/** Short status for a chip: Waiting, Accepted, Declined, Cancelled. */
export const SPLIT_STATUS: Record<TxSplitDto['status'], string> = { pending: 'Waiting', accepted: 'Accepted', declined: 'Declined', cancelled: 'Cancelled' };

/** Toasts and refusals. */
/** After a purchase is saved or confirmed with "Split this bill" on: sent, or why not. */
export const SPLIT_SENT = (partner: string) => `Split request sent to ${partner}.`;
export const SPLIT_NOT_SENT = (done: 'Saved' | 'Filed', why: string) =>
  `${done} in full: the split was not sent (${why.replace(/\.$/, '')}). You can split it later from the purchase.`;
/** Why a split was refused when the link is gone (POST /api/splits answers 404). */
export const SPLIT_NO_LINK = 'you are no longer linked';

/** "Costco", "Costco and Shell", "Costco, Shell and Target". */
const nameList = (names: string[]) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);

/**
 * "Confirm all": it never splits and never adds a tip. The toast says how many were filed
 * and, when rows had "Split this bill" on or a tip open, that those were not sent or added, and where.
 */
export function bulkFiledText(filed: number, notSplit: string[], notTipped: string[] = [], notFunded: string[] = []): string {
  const head = filed === 1 ? 'Filed 1 purchase.' : `Filed ${filed} purchases.`;
  const k = notSplit.length;
  const split = k ? ` ${k === 1 ? '1 split was' : `${k} splits were`} not sent. Split ${nameList(notSplit)} from ${k === 1 ? 'the purchase' : 'each purchase'}.` : '';
  // It never adds a tip either: tips open on a row are dropped, and the toast says where to add them.
  const n = notTipped.length;
  const tip = n ? ` ${n === 1 ? '1 tip was' : `${n} tips were`} not added. Add ${n === 1 ? 'it' : 'them'} on ${nameList(notTipped)}.` : '';
  // Nor does it mark anything paid from savings.
  const f = notFunded.length;
  const fund = f ? ` ${f === 1 ? '1 was' : `${f} were`} not marked paid from savings. Mark ${f === 1 ? 'it' : 'them'} on ${nameList(notFunded)}.` : '';
  return head + split + tip + fund;
}

export const SPLIT_CANCELLED_BY_EDIT = (partner: string) => `The split with ${partner} was cancelled because you changed the purchase. The full amount is back in your budget.`;
/** After a tip or amount change on a waiting split: cancelled, then sent again (by the app, POST /api/splits). */
export const SPLIT_RESENT = (partner: string) => `The split with ${partner} was sent again with the new total.`;
/**
 * A new total on a waiting split that could not go out again: nothing is saved and the request
 * stays as it was (PATCH /api/transactions/:id answers 409 before any write). Reasons below.
 */
export const SPLIT_KEPT = (partner: string, why: string) => `Not saved, so your split with ${partner} stays as it was: ${why.replace(/\.$/, '')}.`;
export const RESEND_NO_LINK = (partner: string) => `you are no longer linked with ${partner}`;
export const RESEND_TOO_SMALL = 'the new amount is too small to split this way';
export const RESEND_SPREAD = 'a yearly purchase spread over 12 months cannot be split';
export const RESEND_SET_ASIDE = 'only spending can be split';
export const RESEND_LIMIT = 'too many split requests this hour, try again later';
/**
 * The hourly cron replaced an auto-logged charge with the real alert while a split of it was open
 * (src/services/placeholder-splits.ts). Same total: the split simply moves (no notice). Otherwise a
 * notice on Overview until OK (split_notices).
 */
const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
export const NOTICE_RESENT = (merchant: string, partner: string, cents: number) =>
  `Hush found the real charge for ${merchant}, ${usd(cents)}, and replaced the one it logged. Your split with ${partner} was sent again for the new total.`;
export const NOTICE_KEPT = (merchant: string, partner: string, why: string) =>
  `Hush found the real charge for ${merchant} but could not move your split with ${partner} to it: ${why.replace(/\.$/, '')}. Both purchases count until you sort it out. Open the logged one to fix it.`;
/** Why a split could not move to the real charge (besides the re-send checks above). */
export const KEPT_REAL_SPLIT = 'the real charge is already split';
export const KEPT_ACCEPTED = (partner: string) => `${partner} already accepted the logged amount`;
export const KEPT_CHANGED = 'the real charge changed while Hush was moving it';
/** Undo of a delete that had cancelled a waiting split (POST /api/transactions/:id/restore). */
export const SPLIT_RESTORED = (partner: string) => `The split request to ${partner} is back too.`;
export const SPLIT_NOT_RESTORED = (partner: string, why: string) =>
  `Restored in full: the split with ${partner} could not come back (${why.replace(/\.$/, '')}). You can split it again from the purchase.`;
export const RESEND_TOO_CHANGED = 'it changed in the meantime';
export const SPLIT_CANCELLED_BY_DELETE = (partner: string) => `The split request to ${partner} was cancelled.`;
export const SPLIT_BLOCKS_CHANGE = (partner: string) =>
  `${partner} accepted a split of this purchase. Remove the split first (it stays in ${partner}'s budget), then change or delete it.`;

/** Emails: plain text, no amounts in the subject, a link to the app only. */
export const splitRequestEmail = (from: string, appUrl: string) => ({
  subject: `${from} sent you a split request in Hush`,
  text: `${from} asked you to split a purchase. Open Hush to see it and accept or decline:\n\n${appUrl}\n\nYou get this email because you linked with ${from} in Hush. Unlink in Settings, Partner.`,
});
export const splitAnswerEmail = (from: string, accepted: boolean, appUrl: string) => ({
  subject: `${from} answered your split request in Hush`,
  text: `${from} ${accepted ? 'accepted' : 'declined'} your split request. Open Hush to see it:\n\n${appUrl}`,
});
